import {
  Button,
  Divider,
  FormControlLabel,
  TextField,
  Checkbox,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  getInputLabelProps,
  getInputProps,
} from "../../../../common/appStyles";
import localize from "../../../../common/localize";
import {
  GetDateInYYYYMMDDHHmmss,
  getEvidenceTypeExtension,
} from "../../../../common/utils";
import Combobox from "../../../../components/common/Combobox";
import PageTitleSubtitle from "../../../../components/PageTitleSubTitle";

export default function FieldEditor({ field, setField }) {
  const inputFile = useRef(null);
  const [fileName, setFileName] = useState(null);

  const getBooleanFieldText = (options, checked) => {
    if (options) {
      const optionsTexts = JSON.parse(options);
      if (optionsTexts) {
        return checked ? optionsTexts.trueText : optionsTexts.falseText;
      }
    }

    return checked ? localize.yes : localize.no;
  };

  const onChangeCheckboxValue = (e) => {
    setField({
      ...field,
      FieldValue: {
        ...field.FieldValue,
        Value: e.target.checked ? "1" : "0",
      },
    });
  };

  const onChangeTextFieldValue = (e) => {
    setField({
      ...field,
      FieldValue: {
        ...field.FieldValue,
        Value: e.target.value,
      },
    });
  };

  const onChangeTextFieldDatetimeValue = (e) => {
    setField({
      ...field,
      FieldValue: {
        ...field.FieldValue,
        Value: GetDateInYYYYMMDDHHmmss(new Date(e.target.value)),
      },
    });
  };

  const onChangeComboboxValue = (e) => {
    if (!e) {
      setField({
        ...field,
        FieldValue: {
          ...field.FieldValue,
          Value: null,
        },
      });
      return;
    }

    setField({
      ...field,
      FieldValue: {
        ...field.FieldValue,
        Value: e.Id,
      },
    });
  };

  const getComboboxOptions = (options) => {
    if (options && options.length > 0) {
      const splittedOptions = options.split(";");
      return splittedOptions.map((element) => ({
        Id: element,
        Name: element,
      }));
    }

    return [];
  };

  const getComboboxValue = (value) => ({
    Id: value,
    Name: value,
  });

  const onButtonClick = () => {
    // `current` points to the mounted file input element
    inputFile.current.click();
  };

  const changeFilePath = (e) => {
    if (e.target.files) {
      setField({
        ...field,
        FieldValue: {
          ...field.FieldValue,
          Value: e.target.files[0],
        },
      });

      setFileName(e.target.files[0].name);
    }
  };

  useEffect(() => {
    if (
      field?.FormField?.ValueType === "DATETIME" &&
      !field?.FormField?.Value
    ) {
      setField({
        ...field,
        FieldValue: {
          ...field.FieldValue,
          Value: GetDateInYYYYMMDDHHmmss(
            field?.FieldValue?.Value
              ? new Date(field?.FieldValue?.Value)
              : new Date()
          ),
        },
      });
    }
  }, []);

  return (
    <div
      style={{ width: "100%", height: "100%" }}
      className="flex-col flex gap-2 mt-2"
    >
      <PageTitleSubtitle
        useEventTitleStyle
        title={field?.FormField?.Title}
        subtitle={field?.FormField?.Description}
      />
      <Divider />
      <div className="w-full" style={{ marginTop: "20px", height: "100%" }}>
        {field?.FormField?.ValueType === "BOOLEAN" ? (
          <FormControlLabel
            control={
              <Checkbox
                onChange={onChangeCheckboxValue}
                checked={field?.FieldValue?.Value === "1"}
                style={{ marginLeft: "10px" }}
              />
            }
            label={getBooleanFieldText(
              field?.FormField?.Options,
              field?.FieldValue?.Value === "1"
            )}
          />
        ) : field?.FormField?.ValueType === "SIGNATURE" ||
          field?.FormField?.ValueType === "PHOTO" ? (
          <div className="flex-col">
            <input
              type="file"
              id="file"
              accept={getEvidenceTypeExtension(1)}
              ref={inputFile}
              onChange={changeFilePath}
              style={{ display: "none" }}
            />
            <Button
              className="w-full"
              color="primary"
              variant="outlined"
              onClick={() => {
                onButtonClick();
              }}
            >
              {localize.upload_image}
            </Button>
            {fileName && (
              <label className="page_description">{`${localize.file}: ${fileName}`}</label>
            )}
          </div>
        ) : field?.FormField?.ValueType === "DATETIME" ? (
          <TextField
            name={`${field?.FieldValue?.Id}`}
            value={GetDateInYYYYMMDDHHmmss(
              field?.FieldValue?.Value
                ? new Date(field?.FieldValue?.Value)
                : new Date()
            )}
            onChange={onChangeTextFieldDatetimeValue}
            style={{
              width: "100%",
            }}
            size="small"
            type="datetime-local"
            InputLabelProps={getInputLabelProps()}
            inputProps={{
              ...getInputProps(),
              placeholder: "yyyy-MM-dd HH:mm",
            }}
          />
        ) : field?.FormField?.ValueType === "DATE" ? (
          <TextField
            name={`${field?.FieldValue?.Id}`}
            value={field?.FieldValue?.Value}
            onChange={onChangeTextFieldValue}
            style={{
              width: "100%",
            }}
            size="small"
            type="date"
            InputLabelProps={getInputLabelProps()}
            inputProps={{
              ...getInputProps(),
              placeholder: "yyyy-mm-dd",
            }}
          />
        ) : field?.FormField?.ValueType === "COMBOBOX" ? (
          <Combobox
            id="FieldValue"
            handleValueChange={onChangeComboboxValue}
            fieldLabel={localize.select_value}
            fieldOptions={getComboboxOptions(field?.FormField?.Options)}
            fieldValue={getComboboxValue(field?.FieldValue?.Value ?? "")}
          />
        ) : (
          <TextField
            name={`${field?.FieldValue?.Id}`}
            value={field?.FieldValue?.Value}
            multiline={field?.FormField?.Multiline}
            rows={field?.FormField?.Multiline ? 16 : 1}
            maxRows={field?.FormField?.Multiline ? 16 : 1}
            type={
              field?.FormField?.ValueType === "INT" ||
              field?.FormField?.ValueType === "DOUBLE"
                ? "number"
                : "text"
            }
            onChange={onChangeTextFieldValue}
            style={{
              width: "100%",
            }}
            size="small"
            InputLabelProps={getInputLabelProps()}
            inputProps={{
              ...getInputProps(),
            }}
            step={field?.FormField?.ValueType === "DOUBLE" ? "0.01" : "1"}
            sx={
              field?.FormField?.Multiline
                ? {
                    flex: 1,
                    height: "100%",
                    "& .MuiFormControl-root": {
                      height: "100% !important",
                    },
                    "& .MuiInputBase-root": {
                      height: "100% !important",
                      alignItems: "flex-start",
                    },
                    "& textarea": {
                      height: "95% !important",
                      overflow: "auto",
                      resize: "none"
                    },
                  }
                : undefined
            }
          />
        )}
      </div>
    </div>
  );
}
